import React, { useRef, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { tokens } from "../theme";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CustomTextField from "../components/ui/Textfield";
import CustomSelect from "../components/ui/Select";
import { useSelector } from "react-redux";
import { RootState } from "../store/Index";
import apiService from "../service/Api";
import { ToolAsset } from "./ToolsAssetsDrawer";
import { createResource, getResourceCategories } from "../pages/resources/resourcesApi";
import { ResourceCategory, ResourceItem } from "../pages/resources/types";
import { toast } from "react-toastify";

interface AddAssetDrawerProps {
  open: boolean;
  onClose: () => void;
  onAssetAdded?: (asset: ToolAsset) => void;
  onResourceAdded?: (resource: ResourceItem) => void;
}

const AddAssetDrawer: React.FC<AddAssetDrawerProps> = ({
  open,
  onClose,
  onAssetAdded,
  onResourceAdded,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const [assetName, setAssetName] = useState("");
  const [category, setCategory] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [resourceCategories, setResourceCategories] = useState<ResourceCategory[]>([]);
  const [resourceCategory, setResourceCategory] = useState("");
  const [resourceDescription, setResourceDescription] = useState("");
  const [resourceUrl, setResourceUrl] = useState("");
  const [resourceTags, setResourceTags] = useState("");
  const [resourceSource, setResourceSource] = useState<"link" | "file">("link");
  const [resourceFile, setResourceFile] = useState<File | null>(null);
  const categoryMap: Record<string, string> = {
    "Brand Assets": "brand_asset",
    Logos: "logo",
    "Lower Thirds": "lower_third",
    Images: "image",
    Videos: "video",
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!open || category !== "Resources" || resourceCategories.length > 0) return;
    getResourceCategories().then(setResourceCategories).catch(() => {
      setResourceCategories([]);
      toast.error("Categories could not be loaded.");
    });
  }, [open, category, resourceCategories.length]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const formatFileSize = (sizeInBytes: number) => {
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024)
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleCloseDrawer = () => {
    setAssetName("");
    setCategory("");
    setUploadedFile(null);
    setResourceCategory("");
    setResourceDescription("");
    setResourceUrl("");
    setResourceTags("");
    setResourceSource("link");
    setResourceFile(null);
    onClose();
  };
  const detectResourceType = (url: string): ResourceItem["resource_type"] => {
    const value = url.toLowerCase();
    if (value.includes("docs.google.com/spreadsheets") || value.includes("sheets.google.com")) return "google_sheets";
    if (value.includes("docs.google.com/document")) return "google_docs";
    if (value.includes("drive.google.com")) return "google_drive";
    if (value.endsWith(".pdf") || value.includes(".pdf?")) return "pdf";
    return "website";
  };
  const slugify = (value: string) => value.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleSaveAsset = async () => {
    if (category === "Resources") {
      if (!assetName || !resourceCategory || (resourceSource === "link" ? !resourceUrl : !resourceFile)) {
        toast.error("Please enter the required document or link details.");
        return;
      }
      try {
        let payload: FormData | Parameters<typeof createResource>[0];
        if (resourceSource === "file" && resourceFile) {
          if (resourceFile.size > 10 * 1024 * 1024) {
            toast.error("Document files cannot exceed 10 MB.");
            return;
          }
          const extension = resourceFile.name.split(".").pop()?.toLowerCase();
          const fileType: ResourceItem["resource_type"] = extension === "pdf" ? "pdf" : "website";
          const formData = new FormData();
          formData.append("title", assetName.trim());
          formData.append("slug", `${slugify(assetName)}-${Date.now().toString().slice(-6)}`);
          formData.append("description", resourceDescription.trim());
          formData.append("tags", JSON.stringify(resourceTags.split(",").map((tag) => tag.trim().toLowerCase()).filter(Boolean)));
          formData.append("category", resourceCategory);
          formData.append("resource_type", fileType);
          formData.append("file", resourceFile);
          formData.append("is_featured", "false");
          formData.append("is_published", "true");
          payload = formData;
        } else {
          payload = {
            title: assetName.trim(),
            slug: `${slugify(assetName)}-${Date.now().toString().slice(-6)}`,
            description: resourceDescription.trim(),
            tags: resourceTags.split(",").map((tag) => tag.trim().toLowerCase()).filter(Boolean),
            category: Number(resourceCategory),
            resource_type: detectResourceType(resourceUrl),
            url: resourceUrl.trim(),
            is_featured: false,
            is_published: true,
          };
        }
        const resource = await createResource(payload);
        onResourceAdded?.(resource);
        toast.success("Document or link added successfully.");
        handleCloseDrawer();
      } catch (error) {
        console.error("Failed to add resource", error);
        toast.error("Failed to add the document or link.");
      }
      return;
    }

    if (!assetName || !category || !uploadedFile) {
      toast.error("Please fill all fields and upload a file.");
      return;
    }

    const formData = new FormData();
    formData.append("name", assetName);
    formData.append("asset_type", categoryMap[category]);

    if (category === "Logos") {
      formData.append("image", uploadedFile);
    } else {
      formData.append("file", uploadedFile);
    }

    formData.append("description", "");
    formData.append("is_active", "");

    try {
      const response = await apiService.post<ToolAsset>(
        "/api/configurations/tools-assets/",
        formData,
      );

      console.log("Asset uploaded successfully:", response.data);

      // ✅ THIS IS WHERE IT GOES
      if (onAssetAdded) {
        onAssetAdded(response.data);
      }

      toast.success("Asset uploaded successfully.");
      handleCloseDrawer();
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("Failed to upload asset:", error.message);
      } else {
        console.error("Failed to upload asset:", error);
      }
      toast.error("Failed to upload asset.");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseDrawer}
      PaperProps={{
        sx: {
          width: { xs: "80%", sm: 400 },
          p: 3,
          backgroundColor: colors.neutral[900],
          color: colors.primary[700],
        },
      }}
    >
      <IconButton
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1,
          bgcolor: colors.primary[900],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleCloseDrawer}
      >
        <ArrowBackIosIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{ mb: 3, mt: 2, fontSize: 24 }}
      >
        {category === "Resources" ? "Add New Doc or Link" : "Add New Asset"}
      </Typography>

      <CustomTextField
        type="text"
        value={assetName}
        onChange={(e) => setAssetName(e.target.value)}
        placeholder={category === "Resources" ? "Document or Link Name" : "Asset Name"}
        sx={{ mb: 2 }}
      />

      <CustomSelect
        label="Select Category"
        value={category}
        onChange={(e) => setCategory(e.target.value as string)}
        sx={{ mb: 2 }}
      >
        <MenuItem value="Brand Assets">Brand Assets</MenuItem>
        <MenuItem value="Logos">Logos</MenuItem>
        <MenuItem value="Resources">Docs &amp; Links</MenuItem>
        <MenuItem value="Lower Thirds">Lower Thirds</MenuItem>
        <MenuItem value="Images">Images</MenuItem>
        <MenuItem value="Videos">Videos</MenuItem>
      </CustomSelect>

      {category === "Resources" ? (
        <Stack spacing={2.25}>
          <Typography fontWeight={700}>Document or link details</Typography>
          <TextField
            label="Description"
            multiline
            minRows={2}
            value={resourceDescription}
            onChange={(e) => setResourceDescription(e.target.value)}
            placeholder="Briefly describe this document or link"
            fullWidth
          />
          <TextField
            select
            label="Category"
            value={resourceCategory}
            onChange={(e) => setResourceCategory(e.target.value as string)}
            fullWidth
          >
            {resourceCategories.map((item) => (
              <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={resourceSource}
            onChange={(_, value) => value && setResourceSource(value)}
            size="small"
          >
            <ToggleButton value="link">Link</ToggleButton>
            <ToggleButton value="file">Upload document</ToggleButton>
          </ToggleButtonGroup>
          {resourceSource === "link" ? (
            <TextField
              type="url"
              label="Link URL"
              value={resourceUrl}
              onChange={(e) => setResourceUrl(e.target.value)}
              placeholder="https://..."
              fullWidth
            />
          ) : (
            <Button
              component="label"
              variant="outlined"
              sx={{ minHeight: 82, borderStyle: "dashed", textTransform: "none", display: "flex", flexDirection: "column" }}
            >
              {resourceFile ? resourceFile.name : "Choose a document"}
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                PDF, Word, Excel, PowerPoint, TXT or CSV · maximum 10 MB
              </Typography>
              <input
                hidden
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                onChange={(event) => setResourceFile(event.target.files?.[0] || null)}
              />
            </Button>
          )}
          <TextField
            label="Tags"
            value={resourceTags}
            onChange={(e) => setResourceTags(e.target.value)}
            placeholder="recruitment, onboarding"
            helperText="Optional · separate tags with commas"
            fullWidth
          />
        </Stack>
      ) : (
      <>
      <Box
        sx={{
          width: "100%",
          height: 200,
          border: `2px solid ${colors.neutral[800]}`,
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          mb: 2,
          textAlign: "center",
          "&:hover": { opacity: 0.8 },
        }}
        onClick={() => inputRef.current?.click()}
      >
        <IconButton aria-label="Tool assets">
          <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 38 }} />
        </IconButton>
        <Typography variant="body2">Click or drag to upload a file</Typography>

        <Button
          variant="outlined"
          sx={{
            textTransform: "none",
            mt: 2,
            border: `1px solid ${colors.neutral[700]}`,
            color: colors.neutral[700],
            borderRadius: 2,
          }}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Browse Files
        </Button>
      </Box>

      <input
        type="file"
        ref={inputRef}
        style={{ display: "none" }}
        accept="image/*,video/*"
        onChange={handleFileChange}
      />

      {uploadedFile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            mb: 1,
            border: `1px solid ${colors.primary[700]}`,
            borderRadius: 1,
            height: 60,
            backgroundColor: colors.neutral[900],
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1,
              backgroundColor: "rgba(8, 172, 30, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 3,
              fontSize: "0.75rem",
            }}
          >
            {uploadedFile.name.split(".").pop()?.toUpperCase()}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {uploadedFile.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.neutral[700],
                display: "block",
                mb: 2,
                opacity: 0.7,
              }}
            >
              {formatFileSize(uploadedFile.size)} of{" "}
              {formatFileSize(uploadedFile.size)}
            </Typography>
          </Box>
        </Box>
      )}
      </>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          sx={{
            borderRadius: 2,
            mt: 6,
            color: colors.primary[900],
            backgroundColor: colors.primary[400],
            width: 120,
          }}
          onClick={handleSaveAsset}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddAssetDrawer;
